import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import RoleBasedSidebar from '../../components/RoleBasedSidebar';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/ReportsPage.css';
import '../../styles/DashboardLayout.css';

const ReportsListPage = () => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('classes');
  const [studentSearch, setStudentSearch] = useState('');
  
  // Get user context to determine admin vs teacher routing
  const { user } = useContext(AuthContext);

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all classes (halaqas)
        const classesResponse = await api.get('/halaqas');
        
        const classesData = classesResponse.data.success 
          ? classesResponse.data.data 
          : classesResponse.data;
        
        const classesArray = Array.isArray(classesData) ? classesData : [];
        
        // Fetch student counts for each class
        const classesWithCounts = await Promise.all(
          classesArray.map(async (classItem) => {
            try {
              // Fetch students for each class
              const studentsResponse = await api.get(`/halaqas/${classItem._id}/students`);
              const studentsData = studentsResponse.data.success
                ? studentsResponse.data.data
                : studentsResponse.data;
              
              // Return class with student count added
              return {
                ...classItem,
                studentCount: Array.isArray(studentsData) ? studentsData.length : 0
              };
            } catch (err) {
              console.error(`Error fetching students for class ${classItem._id}:`, err);
              return {
                ...classItem,
                studentCount: 0
              };
            }
          })
        );
        
        setClasses(classesWithCounts);
        
        // Fetch all students for student reports tab
        const allStudentsResponse = await api.get('/users?role=student');
        const allStudentsData = allStudentsResponse.data.success
          ? allStudentsResponse.data.data
          : allStudentsResponse.data;
        
        // Get class names for each student
        const studentsWithClasses = await Promise.all(
          (Array.isArray(allStudentsData) ? allStudentsData : []).map(async (student) => {
            const studentClasses = [];
            
            // Get class names from halaqas array
            if (student.studentInfo?.halaqas && student.studentInfo.halaqas.length > 0) {
              for (const halaqaId of student.studentInfo.halaqas) {
                const classItem = classesArray.find(c => c._id === halaqaId);
                if (classItem) {
                  studentClasses.push(classItem.name);
                }
              }
            }
            // Fallback to primary class
            else if (student.studentInfo?.halaqaId) {
              const classItem = classesArray.find(c => c._id === student.studentInfo.halaqaId);
              if (classItem) {
                studentClasses.push(classItem.name);
              }
            }
            
            return {
              ...student,
              classNames: studentClasses
            };
          })
        );
        
        setStudents(studentsWithClasses);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  // Filter students based on search
  const filteredStudents = students.filter(student => 
    student.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    student.classNames.some(className => 
      className.toLowerCase().includes(studentSearch.toLowerCase())
    )
  );

  return (
    <div className="dashboard-container">
      {/* Mobile Header - without hamburger menu */}
      <div className="mobile-header">
        <h1 className="app-title">Reports</h1>
        <div className="avatar">👤</div>
      </div>

      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        
        {/* Main Content */}
        <div className="dashboard-content">
          <div className="reports-container">
            <div className="header">
              <div className="header-left">
                <Link to="/dashboard" className="back-to-dashboard">
                  &larr; Back to Dashboard
                </Link>
                <h1>Reports</h1>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="report-tabs">
              <button 
                className={`tab-button ${activeTab === 'classes' ? 'active' : ''}`}
                onClick={() => setActiveTab('classes')}
              >
                Class Reports
              </button>
              <button 
                className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
                onClick={() => setActiveTab('students')}
              >
                Student Reports
              </button>
            </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          {/* Class Reports Tab */}
          {activeTab === 'classes' && (
            <div className="reports-grid">
              {classes.length > 0 ? (
                classes.map(classItem => (
                  <div 
                    key={classItem._id} 
                    className={`report-card ${classItem.active ? 'active' : 'inactive'}`}
                  >
                    <div className="report-header">
                      <h2>{classItem.name}</h2>
                      <span className={`status-badge ${classItem.active ? 'status-active' : 'status-inactive'}`}>
                        {classItem.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="report-meta">
                      <div className="meta-item">
                        <span className="label">Students:</span>
                        <span className="value">{classItem.studentCount || 0}</span>
                      </div>
                      <div className="meta-item">
                        <span className="label">Teachers:</span>
                        <span className="value">{classItem.teachers?.length || 0}</span>
                      </div>
                    </div>
                    
                    <div className="report-actions">
                      <Link to={`/reports/class/${classItem._id}`} className="view-report-btn">
                        View Class Report
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <p>No classes found. Create a class to generate reports.</p>
                  <Link to="/classes/new" className="create-class-btn">
                    Create New Class
                  </Link>
                </div>
              )}
            </div>
          )}
          
          {/* Student Reports Tab */}
          {activeTab === 'students' && (
            <>
              {/* Search bar */}
              <div className="student-search">
                <input
                  type="text"
                  placeholder="Search students by name or class..."
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="students-grid">
                {filteredStudents.length > 0 ? (
                  filteredStudents.map(student => (
                    <div key={student._id} className="student-card">
                      <div className="student-header">
                        <h3>{student.name}</h3>
                      </div>
                      
                      <div className="student-meta">
                        <div className="meta-item">
                          <span className="label">Classes:</span>
                          <span className="value">
                            {student.classNames.length > 0 
                              ? student.classNames.join(', ') 
                              : 'No classes assigned'
                            }
                          </span>
                        </div>
                      </div>
                      
                      <div className="student-actions">
                        <Link 
                          to={user?.role === 'admin' || user?.role === 'super_admin' 
                            ? `/admin/students/${student._id}` 
                            : `/students/${student._id}`
                          } 
                          className="view-report-btn"
                        >
                          View Student Report
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <p>
                      {studentSearch 
                        ? `No students found matching "${studentSearch}"`
                        : 'No students found. Create students to generate reports.'
                      }
                    </p>
                    {!studentSearch && (
                      <Link to="/users/new" className="create-class-btn">
                        Add New Student
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsListPage;